"use client";

import { useState, useEffect, useRef, startTransition } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import DOMPurify from 'dompurify';
import { useCart } from '@/context/CartContext';
import { applyFreeProductPrice, isProductAvailable, isFreeProduct } from '@/utils/productUtils';
import { Product } from '@/types';
import QuantityStepper from '@/components/QuantityStepper';
import SimilarProductsSection from '@/components/SimilarProductsSection';
import ShareModal from '@/components/ShareModal';
import Breadcrumbs from '@/components/Breadcrumbs';
import ReviewsCarousel from '@/components/ReviewsCarousel';
import {
  getProductAnalytics,
  trackAddToCartClicked,
  trackEvent,
  trackProductViewed,
} from '@/lib/posthog';
import {
  IoStar,
  IoCart,
  IoSparkles,
  IoSyncCircleOutline,
  IoRocketOutline,
  IoShieldCheckmarkOutline,
  IoCheckmarkCircle,
  IoFlame,
  IoFlash,
  IoFlask,
  IoChevronForward,
  IoChevronBack,
  IoAlertCircle,
  IoShareSocialOutline
} from 'react-icons/io5';

// Import Swiper for the image gallery
import { Swiper, SwiperSlide } from 'swiper/react';
import { Thumbs, Autoplay, Pagination, Navigation } from 'swiper/modules';
import type { Swiper as SwiperInstance } from 'swiper/types';
import 'swiper/css';
import 'swiper/css/thumbs';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

// --- Sub-components ---

const AccordionItem = ({ title, children, defaultOpen = false, icon, onOpen }: { title: string; children: React.ReactNode; defaultOpen?: boolean; icon?: React.ReactNode; onOpen?: () => void }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const handleToggle = () => {
    const nextOpen = !isOpen;
    setIsOpen(nextOpen);
    if (nextOpen) onOpen?.();
  };
  return (
    <div className="border-b border-gray-100 last:border-0">
      <button onClick={handleToggle} className="w-full flex justify-between items-center py-5 text-left group">
        <div className="flex items-center gap-3">
          {icon && <span className="text-primary text-xl">{icon}</span>}
          <span className="font-bold text-lg text-accent group-hover:text-primary transition-colors">{title}</span>
        </div>
        <span className={`text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>▼</span>
      </button>
      <div className={`grid transition-all duration-200 ease-in-out ${isOpen ? 'grid-rows-[1fr] pb-4 opacity-100' : 'grid-rows-[0fr] opacity-0'} `}>
        <div className="overflow-hidden text-text-secondary leading-relaxed">
          {children}
        </div>
      </div>
    </div>
  );
};

const StarRating = ({ rating, size = 'sm' }: { rating: number; size?: 'sm' | 'md' | 'lg' }) => {
  let starSize = 'w-4 h-4';
  if (size === 'md') starSize = 'w-5 h-5';
  if (size === 'lg') starSize = 'w-6 h-6';

  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }, (_, i) => (
        <IoStar key={i} className={`${starSize} ${i < Math.round(rating) ? 'text-yellow-400' : 'text-gray-200'} `} />
      ))}
    </div>
  );
};

type ProductReview = {
  userName: string;
  rating: number;
  review: string;
  createdAt?: string;
};

const PRODUCT_OFFER_TIMER_SECONDS = (12 * 60 * 60) + (16 * 60);

const CountdownTimer = () => {
  const [timeLeft, setTimeLeft] = useState(PRODUCT_OFFER_TIMER_SECONDS);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMounted(true);
    // Check session storage for existing target time
    const storedTarget = sessionStorage.getItem('offerTargetTime');
    let targetTime: number;

    if (storedTarget) {
      targetTime = parseInt(storedTarget, 10);
    } else {
      // Set new target time
      targetTime = Date.now() + (PRODUCT_OFFER_TIMER_SECONDS * 1000);
      sessionStorage.setItem('offerTargetTime', targetTime.toString());
    }

    const updateTimer = () => {
      const now = Date.now();
      const diff = Math.max(0, Math.floor((targetTime - now) / 1000));
      setTimeLeft(diff);
    };

    // Initial update
    updateTimer();

    const intervalId = setInterval(updateTimer, 1000);
    return () => clearInterval(intervalId);
  }, []);

  if (!mounted) {
    return <div className="h-[62px] w-full rounded-lg border border-orange-100 bg-orange-50" />;
  }

  const hours = Math.floor(timeLeft / 3600);
  const minutes = Math.floor((timeLeft % 3600) / 60);
  const seconds = timeLeft % 60;

  return (
    <div className="flex w-full items-center justify-between gap-2 rounded-lg border border-orange-100 bg-orange-50 px-3 py-2.5 text-accent">
      <div className="flex items-center gap-2">
        <div className="rounded-full bg-white p-1.5 text-primary">
          <IoFlame className="text-base" />
        </div>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-primary">Limited Time Offer</p>
          <p className="text-xs font-semibold sm:text-sm">Price drops ends in</p>
        </div>
      </div>
      <div className="flex gap-1.5 text-center sm:gap-2">
        <div className="min-w-8 rounded-md bg-white px-1.5 py-1 text-primary sm:min-w-[38px] sm:px-2">
          <span className="block text-base font-black leading-none sm:text-lg">{String(hours).padStart(2, '0')}</span>
          <span className="text-[9px] font-bold uppercase">Hrs</span>
        </div>
        <span className="mt-1 self-start font-bold text-primary/60">:</span>
        <div className="min-w-8 rounded-md bg-white px-1.5 py-1 text-primary sm:min-w-[38px] sm:px-2">
          <span className="block text-base font-black leading-none sm:text-lg">{String(minutes).padStart(2, '0')}</span>
          <span className="text-[9px] font-bold uppercase">Min</span>
        </div>
        <span className="mt-1 self-start font-bold text-primary/60">:</span>
        <div className="min-w-8 rounded-md bg-white px-1.5 py-1 text-primary sm:min-w-[38px] sm:px-2">
          <span className="block text-base font-black leading-none sm:text-lg">{String(seconds).padStart(2, '0')}</span>
          <span className="text-[9px] font-bold uppercase">Sec</span>
        </div>
      </div>
    </div>
  );
};

// --- MAIN CLIENT COMPONENT ---
export default function ProductDetailClient({ product }: { product: Product }) {
  const { addToCart, updateQuantity, getItemQuantity, openCartDrawer } = useCart();
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const router = useRouter();

  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperInstance | null>(null);
  const [isMainActionsVisible, setIsMainActionsVisible] = useState(true);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const mainSwiperRef = useRef<SwiperInstance | null>(null);
  const mobileActionsRef = useRef<HTMLDivElement>(null);
  const desktopActionsRef = useRef<HTMLDivElement>(null);
  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const trackedProductRef = useRef<string | null>(null);

  useEffect(() => {
    const nodes = [mobileActionsRef.current, desktopActionsRef.current].filter(Boolean) as HTMLDivElement[];
    if (nodes.length === 0) return;

    const visibility = new Map<Element, boolean>();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => visibility.set(entry.target, entry.isIntersecting));
        setIsMainActionsVisible(Array.from(visibility.values()).some(Boolean));
      },
      { threshold: 0.35 }
    );

    nodes.forEach((node) => {
      visibility.set(node, false);
      observer.observe(node);
    });

    return () => observer.disconnect();
  }, [product?.id]);

  useEffect(() => {
    if (!product || trackedProductRef.current === product.id) {
      return;
    }

    trackedProductRef.current = product.id;
    trackProductViewed(applyFreeProductPrice(product), {
      time_to_view_ms: Math.round(performance.now()),
    });
  }, [product]);

  const displayProduct = applyFreeProductPrice(product);

  const quantityInCart = getItemQuantity(displayProduct.id);
  const isAvailable = isProductAvailable(product);
  const discountPercent = displayProduct.compare_at_price ? Math.round(((displayProduct.compare_at_price - displayProduct.price) / displayProduct.compare_at_price) * 100) : 0;
  const imageGallery = Array.isArray(displayProduct.image_url) ? displayProduct.image_url : (displayProduct.image_url ? [displayProduct.image_url] : []);
  
  const rawDescription = displayProduct.full_description || displayProduct.name;
  const sanitizedDescription = typeof window !== 'undefined' ? DOMPurify.sanitize(rawDescription) : rawDescription;


  const isAghoriLocket = displayProduct.slug === 'astroji-premium-aghori-evil-eye-locket-9c1efe';
  const aghoriFacts = [
    ['Type', 'Energized evil-eye pendant'],
    ['Use', 'Daily spiritual wear'],
    ['Care', 'Keep dry; avoid perfume'],
    ['Dispatch', '24-48 hours'],
  ];

  const pillColors = [
    'bg-blue-50 text-blue-700 border-blue-100',
    'bg-purple-50 text-purple-700 border-purple-100',
    'bg-green-50 text-green-700 border-green-100',
    'bg-orange-50 text-orange-700 border-orange-100',
    'bg-pink-50 text-pink-700 border-pink-100',
    'bg-teal-50 text-teal-700 border-teal-100',
  ];

  const handleAddToCart = (elementSection: string) => {
    trackAddToCartClicked(displayProduct, {
      from_page: 'product_detail',
      element_section: elementSection,
      quantity: 1,
      cart_status_before: quantityInCart,
    });
    startTransition(() => {
      addToCart(displayProduct);
    });
  };

  const handleBuyNow = () => {
    trackEvent('buy_now_clicked', getProductAnalytics(displayProduct, {
      from_page: 'product_detail',
      cart_status_before: quantityInCart,
    }));
    if (quantityInCart === 0) {
      trackAddToCartClicked(displayProduct, {
        from_page: 'product_detail',
        element_section: 'buy_now',
        quantity: 1,
        cart_status_before: quantityInCart,
      });
      startTransition(() => {
        addToCart(displayProduct);
      });
    }
    router.push('/cart');
  };

  const handleShareOpen = () => {
    trackEvent('share_modal_opened', {
      share_location: 'product',
      product_id: displayProduct.id,
      product_name: displayProduct.name,
    });
    setIsShareModalOpen(true);
  };

  const breadcrumbItems = [
    { label: 'Products', href: '/products' },
    { label: displayProduct.name },
  ];

  return (
    <div className="min-h-screen bg-background pb-24 lg:pb-0">
      <div className="ui-container pt-4 sm:pt-6">
        <Breadcrumbs items={breadcrumbItems} className="mb-4" />

        <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-12 lg:gap-8">

          {/* --- LEFT COLUMN: Images (6 cols) --- */}
          <div className="lg:col-span-6 lg:sticky lg:top-24 lg:h-fit">
            <div className="relative group mb-3 sm:mb-4">
              {/* Navigation Arrows */}
              <button
                ref={prevRef}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full shadow-lg flex items-center justify-center text-accent hover:bg-primary hover:text-white transition-all opacity-0 group-hover:opacity-100"
                aria-label="Previous image"
              >
                <IoChevronBack size={20} />
              </button>

              <button
                ref={nextRef}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full shadow-lg flex items-center justify-center text-accent hover:bg-primary hover:text-white transition-all opacity-0 group-hover:opacity-100"
                aria-label="Next image"
              >
                <IoChevronForward size={20} />
              </button>

              <Swiper
                modules={[Thumbs, Autoplay, Pagination, Navigation]}
                onSwiper={(swiper) => {
                  mainSwiperRef.current = swiper;
                }}
                onSlideChange={(swiper) => {
                  setActiveImageIndex(swiper.realIndex);
                }}
                thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }}
                loop={true}
                autoplay={{ delay: 5000, disableOnInteraction: false }}
                pagination={{ clickable: true, dynamicBullets: true }}
                navigation={true}
                onBeforeInit={(swiper) => {
                  if (typeof swiper.params.navigation !== 'boolean') {
                    const navigation = swiper.params.navigation;
                    if (navigation) {
                      navigation.prevEl = prevRef.current;
                      navigation.nextEl = nextRef.current;
                    }
                  }
                }}
                className="w-full aspect-square overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm"
              >
                {imageGallery.map((imgUrl, index) => (
                  <SwiperSlide key={index} className="bg-gray-50">
                    <div className="relative w-full h-full">
                      <Image
                        src={imgUrl}
                        alt={`${displayProduct.name} - Authentic AstroJi Spiritual Product View ${index + 1}`}
                        fill
                        sizes="(max-width: 768px) 100vw, 50vw"
                        preload={index === 0}
                        fetchPriority={index === 0 ? "high" : "low"}
                        loading={index === 0 ? undefined : "lazy"}
                        className="object-contain bg-gray-50"
                      />
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>

              {discountPercent > 0 && (
                <div className="absolute top-4 left-4 z-10 bg-[#D9381E] text-white text-xs font-bold px-3 py-1.5 rounded shadow-md tracking-wide">
                  {discountPercent}% OFF
                </div>
              )}
            </div>

            {/* Thumbnails */}
            {imageGallery.length > 1 && (
              <Swiper
                onSwiper={setThumbsSwiper}
                modules={[Thumbs]}
                spaceBetween={8}
                slidesPerView={5}
                watchSlidesProgress={true}
                className="h-14 sm:h-16"
              >
                {imageGallery.map((imgUrl, index) => (
                  <SwiperSlide
                    key={index}
                    onClick={() => {
                      setActiveImageIndex(index);
                      mainSwiperRef.current?.slideToLoop(index);
                    }}
                    className={`cursor-pointer overflow-hidden rounded-lg border-2 transition-all duration-200 ${activeImageIndex === index ? 'border-primary opacity-100' : 'border-transparent opacity-60 hover:opacity-100'}`}
                  >
                    <div className="relative w-full h-full">
                      <Image
                        src={imgUrl}
                        alt={`${displayProduct.name} thumbnail ${index + 1}`}
                        fill
                        sizes="80px"
                        className="rounded-lg object-cover"
                      />
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            )}
          </div>

          {/* --- RIGHT COLUMN: Product Info (6 cols) --- */}
          <div className="lg:col-span-6">
            <div className="h-full flex flex-col">

              {/* Header Info */}
              <div className="mb-4 rounded-xl border border-gray-100 bg-white p-4 shadow-[0_1px_8px_rgba(15,23,42,0.04)] lg:border-0 lg:bg-transparent lg:p-0 lg:shadow-none lg:border-b lg:pb-4">
                <div className="mb-2 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 text-yellow-400 text-sm">
                      <IoStar />
                      <span className="font-bold text-accent">{displayProduct.rating?.toFixed(1) || '4.8'}</span>
                    </div>
                    <span className="text-gray-300">|</span>
                    <a href="#reviews" className="text-sm text-text-secondary underline decoration-dotted underline-offset-2 transition-colors hover:text-primary">{displayProduct.review_count || 120} Reviews</a>
                  </div>
                  {/* Share Button */}
                  <button
                    onClick={handleShareOpen}
                    className="ui-button-soft min-h-10 rounded-full px-3 py-2"
                  >
                    <IoShareSocialOutline className="text-lg" style={{ strokeWidth: 2 }} />
                    <span className="hidden sm:inline">Share</span>
                  </button>
                </div>

                <h1 className="mb-3 text-xl font-semibold capitalize leading-tight text-accent sm:text-2xl md:text-3xl">{displayProduct.name}</h1>

                {isAghoriLocket && (
                  <div className="mb-4 rounded-2xl border border-amber-200 bg-[#FFF8E8] p-4">
                    <p className="text-sm leading-6 text-stone-800">An energized evil-eye protection pendant intended for everyday spiritual and cultural use.</p>
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      {aghoriFacts.map(([label, value]) => (
                        <div key={label} className="rounded-xl bg-white/80 p-3">
                          <p className="text-[10px] font-bold uppercase tracking-wide text-amber-800">{label}</p>
                          <p className="mt-1 text-xs font-semibold leading-5 text-stone-900">{value}</p>
                        </div>
                      ))}
                    </div>
                    <Link href="/evil-eye/aghori-locket" className="mt-3 inline-flex text-xs font-semibold text-stone-900 underline underline-offset-4">
                      Read meaning, benefits and wearing guide
                    </Link>
                  </div>
                )}

                <div className="mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span className="text-3xl font-bold leading-none text-gray-900">₹{displayProduct.price}</span>
                  {displayProduct.compare_at_price && (
                    <div className="flex items-center gap-2">
                      <span className="text-lg text-gray-400 line-through decoration-red-400">₹{displayProduct.compare_at_price}</span>
                      <span className="text-xs text-green-600 font-bold">Save ₹{displayProduct.compare_at_price - displayProduct.price}</span>
                    </div>
                  )}
                </div>

                {/* Free Product Notification Card */}
                {isFreeProduct(product) && (
                  <div className="mb-5 rounded-xl border border-blue-100 bg-blue-50 p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h4 className="mb-1 flex items-center gap-2 font-semibold text-blue-900">
                          Free gift included
                        </h4>
                        <p className="text-sm text-blue-800 leading-relaxed">
                          You&apos;re getting this product absolutely <span className="font-bold">free of cost</span>. You only need to pay a nominal <span className="font-bold">shipping fee of ₹99</span> to cover delivery charges. No hidden costs!
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <CountdownTimer />

                <div ref={mobileActionsRef} className="mt-3 grid grid-cols-[0.8fr_1.2fr] gap-2 lg:hidden">
                  {!isAvailable ? (
                    <div className="col-span-2 flex min-h-11 items-center justify-center gap-2 rounded-lg bg-gray-100 px-4 py-2.5 text-sm font-semibold text-gray-500">
                      <IoAlertCircle />
                      Out of Stock
                    </div>
                  ) : quantityInCart === 0 ? (
                    <>
                      <button
                        onClick={() => handleAddToCart('mobile_product_summary')}
                        className="ui-button-primary w-full"
                      >
                        <IoCart className="text-lg" />
                        Cart
                      </button>
                      <button
                        onClick={handleBuyNow}
                        className="ui-button-accent w-full"
                      >
                        <IoFlash className="text-lg" />
                        Buy Now
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center justify-center rounded-lg border border-gray-100 bg-gray-50 px-2">
                        <QuantityStepper
                          quantity={quantityInCart}
                          onIncrease={() => updateQuantity(displayProduct.id, quantityInCart + 1)}
                          onDecrease={() => updateQuantity(displayProduct.id, quantityInCart - 1)}
                        />
                      </div>
                      <button onClick={openCartDrawer} className="ui-button-accent w-full">
                        <IoCart className="text-lg" />
                        Go to Cart
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Benefits Tags - Colorful Pills */}
              {displayProduct.benefits && displayProduct.benefits.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-sm font-bold text-accent uppercase tracking-wider mb-3 flex items-center gap-2">
                    <IoSparkles className="text-primary" /> Key Highlights
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {displayProduct.benefits.map((benefit, idx) => (
                      <div
                        key={idx}
                        className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium sm:text-sm ${pillColors[idx % pillColors.length]} `}
                      >
                        <IoCheckmarkCircle className="text-lg opacity-80" />
                        <span>{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Desktop Actions - Inline (Not Sticky) */}
              <div ref={desktopActionsRef} className="ui-card mb-6 hidden p-5 lg:block">
                <div className="flex items-center justify-between mb-4">
                  <span className="font-bold text-accent">Quantity</span>
                  {quantityInCart > 0 ? (
                    <div className="w-[116px]">
                      <QuantityStepper
                        quantity={quantityInCart}
                        onIncrease={() => updateQuantity(displayProduct.id, quantityInCart + 1)}
                        onDecrease={() => updateQuantity(displayProduct.id, quantityInCart - 1)}
                      />
                    </div>
                  ) : !isAvailable ? (
                    <span className="text-red-500 text-sm font-bold flex items-center gap-1"><IoAlertCircle /> Out of Stock</span>
                  ) : (
                    <span className="text-green-600 text-sm font-bold flex items-center gap-1"><IoCheckmarkCircle /> In Stock</span>
                  )}
                </div>

                <div className="flex gap-3">
                  {!isAvailable ? (
                    // Unavailable product - show single disabled button
                    <div className="flex-1 bg-gray-200 text-gray-500 font-bold py-2.5 rounded-lg flex items-center justify-center gap-2 cursor-not-allowed text-sm">
                      <IoAlertCircle className="text-lg" />
                      Out of Stock
                    </div>
                  ) : quantityInCart === 0 ? (
                    <>
                      <button
                        onClick={() => handleAddToCart('product_detail_actions')}
                        className="ui-button-primary flex-1"
                      >
                        <IoCart className="text-lg" />
                        Add to Cart
                      </button>
                      <button
                        onClick={handleBuyNow}
                        className="ui-button-accent flex-1"
                      >
                        <IoFlash className="text-lg" />
                        Buy Now
                      </button>
                    </>
                  ) : (
                    <button onClick={openCartDrawer} className="ui-button-primary flex-1 w-full">
                      <IoCart className="text-lg" />
                      View Cart
                    </button>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-3 gap-2 text-center">
                  <div className="flex flex-col items-center gap-1">
                    <IoShieldCheckmarkOutline className="text-gray-400 text-lg" />
                    <span className="text-[10px] text-gray-500 font-medium">Secure Pay</span>
                  </div>
                  <div className="flex flex-col items-center gap-1 border-l border-gray-100">
                    <IoRocketOutline className="text-gray-400 text-lg" />
                    <span className="text-[10px] text-gray-500 font-medium">Fast Ship</span>
                  </div>
                  <div className="flex flex-col items-center gap-1 border-l border-gray-100">
                    <IoSyncCircleOutline className="text-gray-400 text-lg" />
                    <span className="text-[10px] text-gray-500 font-medium">Easy Returns</span>
                  </div>
                </div>
              </div>

              {/* Trust Badges (Moved Here for better visibility) */}
              <div className="mb-6 grid grid-cols-3 gap-2 rounded-xl border border-orange-100 bg-orange-50/35 p-3">
                <div className="text-center">
                  <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-primary text-xl mx-auto mb-2 shadow-sm"><IoShieldCheckmarkOutline /></div>
                  <p className="text-xs font-bold text-accent">Authentic</p>
                  <p className="text-[10px] text-gray-500">100% Original</p>
                </div>
                <div className="text-center border-l border-orange-200">
                  <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-primary text-xl mx-auto mb-2 shadow-sm"><IoSparkles /></div>
                  <p className="text-xs font-bold text-accent">Energized</p>
                  <p className="text-[10px] text-gray-500">Vedic Rituals</p>
                </div>
                <div className="text-center border-l border-orange-200">
                  <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-primary text-xl mx-auto mb-2 shadow-sm"><IoFlask /></div>
                  <p className="text-xs font-bold text-accent">Lab Tested</p>
                  <p className="text-[10px] text-gray-500">Certified</p>
                </div>
              </div>

              {/* Accordions - Minimalist */}
              <div className="space-y-0 border-t border-gray-100">
                <AccordionItem
                  title="Description"
                  defaultOpen={true}
                >
                  <div className="prose prose-sm max-w-none text-gray-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: sanitizedDescription }} />
                </AccordionItem>

                <AccordionItem
                  title="Shipping Information"
                >
                  <ul className="space-y-3 text-sm text-gray-600">
                    <li className="flex gap-3"><span className="w-1.5 h-1.5 bg-primary rounded-full mt-1.5 flex-shrink-0"></span> <span><strong>Dispatch:</strong> We dispatch orders within 24-48 hours.</span></li>
                    <li className="flex gap-3"><span className="w-1.5 h-1.5 bg-primary rounded-full mt-1.5 flex-shrink-0"></span> <span><strong>Delivery:</strong> Usually takes 3-7 business days.</span></li>
                  </ul>
                </AccordionItem>

                <AccordionItem
                  title="Returns Policy"
                >
                  <p className="text-sm text-gray-600 leading-relaxed">We offer a 7-day replacement policy for damaged items. An unboxing video is mandatory for all claims.</p>
                </AccordionItem>

                {Array.isArray(displayProduct.metaData?.faqs) && displayProduct.metaData.faqs.length > 0 && (
                  <AccordionItem title="Frequently Asked Questions" defaultOpen={false}>
                    <div className="space-y-4 text-sm text-gray-600">
                      {displayProduct.metaData.faqs.map((faq: { question: string; answer: string }, idx: number) => (
                        <div key={idx} className="border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                          <h4 className="font-semibold text-accent mb-1">{faq.question}</h4>
                          <p className="leading-relaxed">{faq.answer}</p>
                        </div>
                      ))}
                    </div>
                  </AccordionItem>
                )}
              </div>

            </div>
          </div>
        </div>

        {/* --- REVIEWS SECTION --- */}
        <div className="mt-12 lg:mt-16">
          <ReviewsCarousel
            reviews={displayProduct.metaData?.reviews || []}
            averageRating={displayProduct.rating || 4.8}
            totalReviews={displayProduct.review_count || (displayProduct.metaData?.reviews?.length ?? 120)}
            title="Customer Reviews"
            badgeText="Verified Feedback"
            containerId="reviews"
            variant="light"
          />
        </div>

        {/* --- SIMILAR PRODUCTS --- */}
        <div className="my-10 lg:mt-24">
          <SimilarProductsSection categoryId={displayProduct.productCategoryId} currentProductId={displayProduct.id} />
        </div>
      </div>

      {/* --- STICKY BOTTOM BAR (Mobile & Desktop) --- */}
      {/* Only show if main actions are NOT visible */}
      {!isMainActionsVisible && (
        <div className="ui-sticky-bar animate-slide-up">
          {/* Discount Ribbon - Only show if there's a discount */}
          {displayProduct.compare_at_price && displayProduct.compare_at_price > displayProduct.price && (
            <div className="overflow-hidden bg-emerald-600 px-4 py-1.5 text-center text-white">
              <p className="text-xs font-bold tracking-wide">
                Limited offer: {Math.round(((displayProduct.compare_at_price - displayProduct.price) / displayProduct.compare_at_price) * 100)}% off. Save ₹{displayProduct.compare_at_price - displayProduct.price} on this item.
              </p>
            </div>
          )}

          <div className="ui-container flex items-center justify-between gap-2 py-2.5 pb-safe sm:gap-3">
            {/* Price Display - Simple */}
            <div className="min-w-[72px] flex-shrink-0 leading-tight sm:flex sm:items-center sm:gap-2">
              <span className="block text-base font-bold text-gray-900 sm:text-lg">₹{displayProduct.price}</span>
              {displayProduct.compare_at_price && <span className="block text-[11px] text-gray-400 line-through sm:text-xs">₹{displayProduct.compare_at_price}</span>}
            </div>

            {/* Action Buttons - More prominent */}
            <div className="flex min-w-0 flex-1 items-center justify-end gap-2 sm:max-w-[300px]">
              {!isAvailable ? (
                <div className="flex-1 bg-gray-200 text-gray-500 font-bold py-2.5 rounded-xl text-xs flex items-center justify-center gap-2 cursor-not-allowed">
                  <IoAlertCircle size={16} />
                  Out of Stock
                </div>
              ) : quantityInCart === 0 ? (
                <>
                  <button
                    onClick={() => handleAddToCart('sticky_product_bar')}
                    className="ui-button-primary w-12 flex-shrink-0 px-0"
                    aria-label="Add to cart"
                  >
                    <IoCart size={16} />
                  </button>
                  <button
                    onClick={handleBuyNow}
                    className="ui-button-accent min-w-0 flex-1 whitespace-nowrap px-3 sm:px-5"
                  >
                    <IoFlash size={16} />
                    Buy Now
                  </button>
                </>
              ) : (
                <div className="flex items-center gap-2 w-full">
                  <div className="w-[116px] flex-shrink-0">
                    <QuantityStepper
                      quantity={quantityInCart}
                      onIncrease={() => updateQuantity(displayProduct.id, quantityInCart + 1)}
                      onDecrease={() => updateQuantity(displayProduct.id, quantityInCart - 1)}
                    />
                  </div>
                  <button onClick={openCartDrawer} className="ui-button-accent flex-1 w-full px-4 text-xs">
                    <IoCart size={16} />
                    Go to Cart
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {typeof window !== 'undefined' && (
        <ShareModal
            isOpen={isShareModalOpen}
            onClose={() => setIsShareModalOpen(false)}
            productName={displayProduct?.name || 'Product'}
            productUrl={window.location.href}
            productImage={displayProduct?.image_url?.[0]}
        />
      )}

    </div>
  );
}
